/* tslint:disable:no-input-prefix */
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChange, SimpleChanges, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import * as Kendo from '@progress/kendo-angular-dialog';

import { BooleanConverter, InputConverter } from '../../../inputConverter';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { LogLevel } from '../../../types';
import { BaseComponent } from '../../base.component';

const instantiateTimeout = 5;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-window',
    styleUrls: ['../../sharedStyles/base.scss', '../../sharedStyles/telerik.scss'],
    templateUrl: './window.component.html'
})
export class WindowComponent extends BaseComponent implements AfterViewInit, OnDestroy, OnChanges {
    get includeGoogleAnalytics() { return false; }

    @ViewChild('kendoWindow') private readonly kendowWindowRef: Kendo.WindowComponent;
    @ViewChild('kendoDialog') private readonly kendoDialogRef: Kendo.DialogComponent;

    @Output() readonly openChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() readonly stateChange: EventEmitter<Kendo.WindowState> = new EventEmitter<Kendo.WindowState>();

    @Input() get windowRef() { return this.modal ? this.kendoDialogRef : this.kendowWindowRef; }

    @Input()
    get isOpened() { return this._isOpened; }
    set isOpened(open: boolean) {
        this._isOpened = BooleanConverter(open);
        if (open) {
            this.setupHeightConstraint();
            this.openChange.emit(open);
            this.LoggingService.Log(`'${this.windowTitle || 'Untitled'}' Window Opened`, this.windowRef, LogLevel.Trace);
        } else {
            this.teardownHeightConstraint();
            this.openChange.emit(open);
            this.LoggingService.Log(`'${this.windowTitle || 'Untitled'}' Window Closed`, this.windowRef, LogLevel.Trace);
        }
        this.cdr.detectChanges();
    }
    private _isOpened = false;

    @Input()
    get disableKeyboard() { return this._disableKeyboard; }
    set disableKeyboard(value: boolean) {
        this._disableKeyboard = BooleanConverter(value);
        this.setKeyboardShortcuts();
    }
    private _disableKeyboard = false;

    @Input()
    @InputConverter()
    modal: boolean = false;

    @Input()
    @InputConverter()
    isResizable: boolean = false;

    @Input()
    @InputConverter()
    isDraggable: boolean = false;

    @Input()
    @InputConverter()
    isMaximizable: boolean = false;

    @Input()
    @InputConverter()
    closable: boolean = true;

    @Input()
    @InputConverter()
    minWidth: number = null;

    @Input()
    @InputConverter()
    maxWidth: number = null;

    @Input()
    width: string | number = null;

    @Input()
    @InputConverter()
    minHeight: number = null;

    @Input()
    @InputConverter()
    maxHeight: number = null;

    @Input()
    height: string | number = null;

    @Input()
    @InputConverter()
    top: number = null;

    @Input()
    @InputConverter()
    left: number = null;

    @Input()
    @InputConverter()
    right: number = null;

    @Input()
    windowTitle: string;

    constructor(
        _vcr: ViewContainerRef,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        private readonly cdr: ChangeDetectorRef,
        elementRef: ElementRef<HTMLElement>
    ) {
        super(_vcr);
        this.registerEvent('openChange', this.openChange);
        if (elementRef.nativeElement.title && !this.windowTitle) {
            this.windowTitle = elementRef.nativeElement.title;
            elementRef.nativeElement.removeAttribute('title');
        }
    }

    ngAfterViewInit() {
        this.isReady();
        this.openChange.subscribe(open => {
            if (open) {
                const instantiateInterval = setInterval(() => {
                    if (this.windowRef) {
                        clearInterval(instantiateInterval);
                        this.setMaxes();
                        this.setKeyboardShortcuts();
                        this.setSize();
                    }
                }, instantiateTimeout);
            }
        });
    }

    ngOnDestroy(): void {
        this.teardownHeightConstraint();
    }

    ngOnChanges(changes: IWindowSimpleChanges): void {
        if (changes.maxWidth || changes.maxHeight || changes.minWidth || changes.minHeight) {
            this.setMaxes();
        }
        if (changes.width || changes.height) {
            this.setSize();
        }
    }

    @Input() close = () => {
        this.isOpened = false;
    }

    setMaxes() {
        if (this.windowRef) {
            const windowDiv = this.getWindowDiv();
            if (windowDiv && !this.modal) {
                this.setDialogMaxes(windowDiv);
            } else if (windowDiv && this.modal) {
                this.setModalMaxes(windowDiv);
            }
        }
    }

    private getWindowDiv(): HTMLDivElement {
        return this.windowRef ?
            this.modal ?
                (<HTMLElement>(<any>this.windowRef)._elRef.nativeElement).querySelector<HTMLDivElement>('.k-widget.k-window')
                : (<HTMLElement>(<any>this.windowRef).el.nativeElement as HTMLDivElement)
            : null;
    }

    private setDialogMaxes(windowDiv: HTMLDivElement) {
        const maxHeight = window.innerHeight;
        const maxWidth = window.innerWidth;
        windowDiv.style.maxHeight = this.maxHeight && this.maxHeight < maxHeight ? `${this.maxHeight}px` : `${maxHeight}px`;
        windowDiv.style.maxWidth = this.maxWidth && this.maxWidth < maxWidth ? `${this.maxWidth}px` : `${maxWidth}px`;
        windowDiv.style.minHeight = this.minHeight ? `${this.minHeight}px` : null;
        windowDiv.style.minWidth = this.minWidth ? `${this.minWidth}px` : null;
        windowDiv.style.left = this.right ? null : windowDiv.style.left;
        windowDiv.style.right = this.right ? `${this.right}px` : null;
    }

    private setModalMaxes(windowDiv: HTMLDivElement) {
        windowDiv.style.maxHeight = null;
        windowDiv.style.maxWidth = null;
        if (!this.height) {
            windowDiv.style.maxHeight = '99vh';
        }
        if (!this.width) {
            windowDiv.style.maxWidth = '99vw';
        }
    }

    private setSize() {
        const windowDiv = this.getWindowDiv();
        if (windowDiv && this.modal) {
            if (this.height) {
                windowDiv.style.height = typeof this.height === 'number' || /^\d+$/.test(this.height) ? `${this.height}px` : this.height;
            }
            if (this.width) {
                windowDiv.style.width = typeof this.width === 'number' || /^\d+$/.test(this.width) ? `${this.width}px` : this.width;
            }
        }
    }

    setKeyboardShortcuts() {
        if (this.windowRef) {
            if (this.disableKeyboard) {
                this.windowRef.tabIndex = null;
            } else {
                this.windowRef.tabIndex = 0;
            }
            this.cdr.detectChanges();
        }
    }

    @Input() open = () => { this.isOpened = true; };

    @Input() toggle = (open?: boolean) => { this.isOpened = open !== undefined ? open : !this.isOpened; };

    private setupHeightConstraint() { window.addEventListener('resize', () => { this.setMaxes(); }); }

    private teardownHeightConstraint() { window.removeEventListener('resize', () => { this.setMaxes(); }); }

    preventEvent(e: MouseEvent, prevent: boolean) {
        if (prevent) {
            e.stopImmediatePropagation();
        }
    }

    stateChangeEvent(event: Kendo.WindowState) {
        this.stateChange.emit(event);
    }
}

interface IWindowSimpleChanges extends SimpleChanges {
    width: SimpleChange;
    height: SimpleChange;
    maxWidth: SimpleChange;
    maxHeight: SimpleChange;
    minWidth: SimpleChange;
    minHeight: SimpleChange;
}
